import { useRef, useLayoutEffect, useMemo, useEffect } from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ChatInput } from '@/components/ChatInput';
import ChatMessageItem from '@/components/ChatMessageItem';
import ArtifactPanel from '@/components/ArtifactPanel';
import type { ChatMessage, UserState } from '@/types';
import type { MessageContext } from '@/types/chat';
import { User } from 'firebase/auth';
import { extractThinkingContent } from '@/utils/thinkingExtractor';
import { useArtifacts } from '@/contexts/ArtifactContext';

export type ChatInterfaceProps = {
  messages: ChatMessage[];
  streamingMessage?: ChatMessage | null;
  // New prop for streaming thinking content
  streamingThinkingContent?: string;
  input: string;
  botName: string;
  botAvatar: string;
  senderName: string;
  userState: UserState;
  onInputChange: (value: string) => void;
  onSendMessage: (messageContext: MessageContext) => void;
  onLogin: () => void;
  isStreaming: boolean;
  error: string | null;
  tools?: string[];
  toolConfigs?: Record<string, Record<string, any>>;
  enableExport?: boolean;
  currentUser: User;
  assistantId: string;
  maxSnippetLength?: number;
  onStopStreaming?: () => void;
  traceId?: string; // Add traceId for Langfuse tracking
  sessionId?: string; // Add sessionId for Langfuse tracking
};

export default function ChatInterface({
  messages,
  streamingMessage = null,
  // Default to empty string for streaming thinking content
  streamingThinkingContent = '',
  input,
  botName,
  botAvatar,
  senderName,
  userState,
  onInputChange,
  onSendMessage,
  onLogin,
  isStreaming,
  error,
  tools,
  toolConfigs,
  enableExport = true,
  currentUser,
  assistantId,
  maxSnippetLength = 80,
  onStopStreaming = () => {},
  traceId,
  sessionId
}: ChatInterfaceProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Use useLayoutEffect to scroll immediately after DOM updates
  useLayoutEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, streamingMessage, streamingThinkingContent]);

  // Set assistant context for Preview component tracking
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any)._assistantContext = {
        assistantId,
        assistantName: botName,
        assistantAvatar: botAvatar
      };
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any)._assistantContext;
      }
    };
  }, [assistantId, botName, botAvatar]);

  // Update viewport meta tag
  useEffect(() => {
    const originalViewport = document
      .querySelector('meta[name="viewport"]')
      ?.getAttribute('content');
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1'
      );
    } else {
      const newViewportMeta = document.createElement('meta');
      newViewportMeta.name = 'viewport';
      newViewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1';
      document.head.appendChild(newViewportMeta);
    }

    return () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }
    };
  }, []);

  // Memoize the sorted list so we avoid re-sorting on every render.
  // Prepare the final messages array to render
  const allMessages = useMemo(() => {
    // If we don't have a streaming message, just return the messages
    if (!streamingMessage) {
      return messages;
    }

    // Since this is a controlled component with state from the parent,
    // we need to be careful about mixing streamed and database messages

    // If the streaming message is empty, don't show it
    if (!streamingMessage.content.trim()) {
      return messages;
    }

    // If streaming is active, replace any completed messages with the same content
    // with our streaming message to avoid duplicates
    if (isStreaming) {
      // Extract clean content from streaming message for comparison
      const { cleanContent: streamingCleanContent } =
        extractThinkingContent(streamingMessage.content);

      // First, check if any message has similar content to our streaming message
      const similarContentIndex = messages.findIndex(msg => {
        if (msg.sender !== streamingMessage.sender) return false;

        // Extract clean content from message
        const { cleanContent: msgCleanContent } = extractThinkingContent(msg.content);

        // Compare clean content
        return msgCleanContent.trim() === streamingCleanContent.trim();
      });

      if (similarContentIndex >= 0) {
        // If we found a similar message, don't add the streaming message
        // as it's likely the same message being streamed
        console.log('Found similar message in messages array, not adding streaming message');
        return messages;
      }

      // If no similar message found, add the streaming message
      return [...messages, streamingMessage];
    } else {
      // If we're not streaming anymore, don't add the streaming message
      // as it should already be in the messages array from Firestore
      return messages;
    }
  }, [messages, streamingMessage, isStreaming]);

  // Inside the renderedMessages useMemo function in ChatInterface.tsx
  const renderedMessages = useMemo(() => {
    // Find the first bot message index
    const firstBotMessageIndex = allMessages.findIndex(msg => msg.sender === 'assistant');
    
    return allMessages.map((message, index) => {
      const isUserMessage =
        message.sender === 'user' ||
        (userState === 'admin' && message.sender === 'admin') ||
        (userState === 'receiver' && message.sender === 'receiver');

      const getAvatarAndName = () => {
        if (message.sender === 'assistant') {
          return {
            image: botAvatar,
            name: botName,
            initials: botName.substring(0, 2),
            userName: message.userName
          };
        }
        if (message.sender === 'admin') {
          return {
            image: message.photoURL,
            name: senderName,
            initials: senderName.substring(0, 2),
            userName: senderName
          };
        }
        const name = message.userName || 'User';
        return {
          image: message.photoURL,
          name,
          initials: name.substring(0, 2),
          userName: name
        };
      };

      const { image, name, initials, userName } = getAvatarAndName();
      
      // Check if this is the first bot message and disable export if it is
      const isFirstBotMessage = !isUserMessage && index === firstBotMessageIndex;
      const shouldEnableExport = enableExport && !isStreaming && !isFirstBotMessage;

      // Determine if this message is the one currently being streamed
      // It should be true only for the streaming message (last message) when isStreaming is true
      const isCurrentlyStreaming = isStreaming && 
                                  streamingMessage && 
                                  message === streamingMessage;

      // If this is the currently streaming message, use the streaming thinking content
      // For historical messages, use the thinking content stored with the message
      // The message object may have thinking content directly attached from extraction
      const thinkingContent = isCurrentlyStreaming
        ? streamingThinkingContent
        : message.thinkingContent || '';

      return (
        <ChatMessageItem
          key={`${message.sender}-${index}-${message.content.substring(0, 20)}`}
          message={message}
          isUserMessage={isUserMessage}
          avatar={image || ''}
          name={name}
          initials={initials}
          enableExport={shouldEnableExport}
          currentUser={currentUser}
          assistantId={assistantId}
          isCurrentlyStreaming={isCurrentlyStreaming || false}
          // Pass thinking content to ChatMessageItem
          thinkingContent={thinkingContent}
          // Use message-specific traceId if available, fall back to conversation traceId
          traceId={message.traceId || traceId}
          // Pass whether this is the last message
          isLastMessage={index === allMessages.length - 1}
          
        />
      );
    });
  }, [
    allMessages, 
    userState, 
    botAvatar, 
    botName, 
    senderName, 
    isStreaming, 
    enableExport, 
    streamingMessage, 
    currentUser, 
    assistantId,
    // Add dependency on streamingThinkingContent
    streamingThinkingContent,
    traceId
  ]);

  // Pass the message context directly.
  const handleSendMessage = (messageContext: MessageContext) => {
    onSendMessage(messageContext);
  };

  return (
    <>
      <div className="flex flex-col h-full bg-white">
        <ScrollArea className="flex-grow overflow-hidden relative"> {/* Added relative */}
          <div
            className="p-4 space-y-4" // Removed w-full, overflow-hidden for this test
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0', // This makes it fill the relative parent
              overflowY: 'auto', // Scrolling now handled by this div
              overflowX: 'hidden', // Still hide horizontal overflow of this div itself
              //border: '2px dotted purple' THE CAUSE OF MUCH CSS STYLING HELL AND PAIN RESULTED IN ABOVE DO NOT REMOVE
            }}
            ref={scrollAreaRef}
          >
            {renderedMessages}
            {error && (
              <div className="flex justify-center">
                <div className="text-red-500 bg-red-100 p-2 rounded">{error}</div>
              </div>
            )}
            <div ref={bottomRef} />
            {isStreaming && <LoadingSpinner
                                isStreaming={isStreaming} 
                                maxSnippetLength={maxSnippetLength}
                                // Pass the thinking content to the LoadingSpinner
                                thinkingContent={streamingThinkingContent}
                              />
            }
          </div>
        </ScrollArea>

        <div className="border-t bg-white">
          <ChatInput
            input={input}
            botName={botName}
            senderName={senderName}
            userState={userState}
            isStreaming={isStreaming}
            onInputChange={onInputChange}
            onSendMessage={handleSendMessage}
            onLogin={onLogin}
            tools={tools}
            toolConfigs={toolConfigs}
            onStopStreaming={onStopStreaming}
          />
        </div>
      </div>
    </>
  );
}